/* Lector del catalogo en PDF.

   Por que existe: el flipbook del worker sirve las paginas como imagenes de
   1200px -unos 104 ppp para una A4- y ademas se pinta a su tamano minimo, asi
   que la letra pequena no se lee. Escalarlo desde fuera tampoco vale: el
   navegador no le propaga la escala al rasterizado de un iframe de otro
   dominio y sale borroso.

   Aqui se pinta el PDF con PDF.js sobre canvas. El texto es vectorial, asi que
   se rasteriza al tamano de la caja y a la densidad de la pantalla: nitido a
   cualquier zoom, sin techo de resolucion. El CDN de Shopify sirve el fichero
   con 'accept-ranges: bytes', asi que PDF.js pide solo los trozos de las
   paginas que se ven en vez de bajarse los 17 MB de golpe.

   Emparejado como el papel: la portada va sola y a partir de ahi 2-3, 4-5...

   El cambio de pagina va suelto porque no se rasteriza al pulsar: las hojas
   vecinas se pintan antes, en tiempo muerto, y quedan en cache. Pasar pagina es
   entonces copiar un bitmap ya hecho -inmediato- mas una transicion corta. */
(() => {
  const CARGADOS = new Set();
  const MAX_CACHE = 10; // hojas rasterizadas que se guardan, las mas recientes

  function cargarScript(src) {
    if (CARGADOS.has(src)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = () => {
        CARGADOS.add(src);
        resolve();
      };
      s.onerror = () => reject(new Error('no se pudo cargar ' + src));
      document.head.appendChild(s);
    });
  }

  const enReposo =
    window.requestIdleCallback || ((fn) => setTimeout(() => fn({ timeRemaining: () => 8 }), 120));

  function iniciar(root) {
    const stage = root.querySelector('[data-stage]');
    const hojas = root.querySelector('[data-pages]');
    const izq = root.querySelector('[data-page-left]');
    const der = root.querySelector('[data-page-right]');
    const estado = root.querySelector('[data-status]');
    const contador = root.querySelector('[data-counter]');
    const btnPrev = root.querySelector('[data-prev]');
    const btnNext = root.querySelector('[data-next]');
    const btnMas = root.querySelector('[data-zoom-in]');
    const btnMenos = root.querySelector('[data-zoom-out]');
    const btnFs = root.querySelector('[data-fullscreen]');
    const url = root.dataset.pdf;
    if (!stage || !url) return;

    const cache = new Map(); // clave de tamano+pagina -> canvas ya rasterizado
    let pdf = null;
    let hoja = 1; // primera pagina del par visible
    let zoom = 1;
    let total = 0;
    let generacion = 0;
    let animando = false;

    const sinMovimiento = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dobleCabe = () => root.dataset.spread !== 'false' && innerWidth >= 1000;

    // La portada va sola; despues los pares (2,3), (4,5)...
    function parDe(n) {
      if (!dobleCabe() || n === 1) return [n, null];
      const izquierda = n % 2 === 0 ? n : n - 1;
      return [izquierda, izquierda + 1 <= total ? izquierda + 1 : null];
    }

    function texto(msg) {
      estado.textContent = msg || '';
      estado.hidden = !msg;
    }

    function medidas() {
      const caja = stage.getBoundingClientRect();
      const relleno = 32;
      const hueco = Math.max(200, caja.width - relleno);
      const alto = Math.max(200, caja.height - relleno);
      const doble = dobleCabe() && hoja !== 1;
      return { ancho: doble ? hueco / 2 : hueco, alto: alto };
    }

    // Rasteriza una pagina a un canvas propio y lo deja en cache. Si ya estaba,
    // vuelve sin tocar PDF.js: eso es lo que hace inmediato el paso de hoja.
    async function rasterizar(num, ancho, alto) {
      if (!num || num > total) return null;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const clave = num + '@' + Math.round(ancho) + 'x' + Math.round(alto) + '@' + zoom + '@' + dpr;
      if (cache.has(clave)) {
        const guardado = cache.get(clave);
        cache.delete(clave); // reordena: el ultimo usado se queda al final
        cache.set(clave, guardado);
        return guardado;
      }

      const page = await pdf.getPage(num);
      const base = page.getViewport({ scale: 1 });
      const encaje = Math.min(ancho / base.width, alto / base.height);
      const vp = page.getViewport({ scale: Math.max(0.1, encaje * zoom) * dpr });

      const lienzo = document.createElement('canvas');
      lienzo.width = Math.floor(vp.width);
      lienzo.height = Math.floor(vp.height);
      const ctx = lienzo.getContext('2d', { alpha: false });
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, lienzo.width, lienzo.height);
      await page.render({ canvasContext: ctx, viewport: vp }).promise;

      cache.set(clave, lienzo);
      if (cache.size > MAX_CACHE) cache.delete(cache.keys().next().value);
      return lienzo;
    }

    function volcar(lienzo, destino) {
      if (!lienzo) {
        destino.hidden = true;
        return;
      }
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      destino.width = lienzo.width;
      destino.height = lienzo.height;
      destino.style.width = Math.floor(lienzo.width / dpr) + 'px';
      destino.style.height = Math.floor(lienzo.height / dpr) + 'px';
      destino.hidden = false;
      destino.getContext('2d', { alpha: false }).drawImage(lienzo, 0, 0);
    }

    function animar(direccion) {
      if (sinMovimiento || !direccion) return;
      const clase = direccion > 0 ? 'pdf-reader__stage--next' : 'pdf-reader__stage--prev';
      stage.classList.remove('pdf-reader__stage--next', 'pdf-reader__stage--prev');
      // Reinicia la animacion aunque se pulse dos veces seguidas.
      void stage.offsetWidth;
      stage.classList.add(clase);
      setTimeout(() => stage.classList.remove(clase), 260);
    }

    async function pintar(direccion) {
      if (!pdf) return;
      const token = ++generacion;
      const { ancho, alto } = medidas();
      const [a, b] = parDe(hoja);

      // Si las hojas ya estan en cache esto resuelve en el mismo cuadro y no
      // llega a verse el aviso de espera.
      const espera = setTimeout(() => {
        if (token === generacion) stage.classList.add('pdf-reader__stage--waiting');
      }, 140);

      let hojaA, hojaB;
      try {
        hojaA = await rasterizar(a, ancho, alto);
        hojaB = await rasterizar(b, ancho, alto);
      } catch (e) {
        clearTimeout(espera);
        if (token === generacion) texto(root.dataset.labelError || '');
        return;
      }
      clearTimeout(espera);
      if (token !== generacion) return;

      stage.classList.remove('pdf-reader__stage--waiting');
      volcar(hojaA, izq);
      volcar(hojaB, der);
      // Cada hoja empieza centrada: heredar el desplazamiento de la anterior
      // deja al lector mirando un margen en blanco sin saber por que.
      reiniciarPan();
      animar(direccion);

      contador.textContent = b ? a + '–' + b + ' / ' + total : a + ' / ' + total;
      btnPrev.disabled = a <= 1;
      btnNext.disabled = (b || a) >= total;
      if (history.replaceState) history.replaceState(null, '', '#pagina=' + a);

      precargarVecinas(token, ancho, alto);
    }

    // Las hojas de al lado se rasterizan en tiempo muerto, para que el
    // siguiente paso de pagina no espere a nada.
    function precargarVecinas(token, ancho, alto) {
      enReposo(async () => {
        if (token !== generacion) return;
        const [a, b] = parDe(hoja);
        const salto = b ? 2 : 1;
        const vecinas = [(b || a) + 1, (b || a) + 2, a - salto, a - salto + 1];
        for (const n of vecinas) {
          if (token !== generacion) return;
          if (n >= 1 && n <= total) {
            try {
              await rasterizar(n, ancho, alto);
            } catch (e) {
              return;
            }
          }
        }
      });
    }

    function mover(paso) {
      if (animando) return;
      const [a, b] = parDe(hoja);
      const salto = b ? 2 : 1;
      const destino = paso > 0 ? (b || a) + 1 : a - salto;
      if (destino < 1 || destino > total) return;
      hoja = destino;
      animando = true;
      setTimeout(() => {
        animando = false;
      }, sinMovimiento ? 0 : 140);
      pintar(paso);
    }

    function cambiarZoom(delta) {
      const nuevo = Math.min(4, Math.max(1, zoom + delta));
      if (nuevo === zoom) return;
      zoom = nuevo;
      cache.clear(); // otra escala son otros bitmaps
      pintar(0);
    }

    btnPrev.addEventListener('click', () => mover(-1));
    btnNext.addEventListener('click', () => mover(1));
    btnMas.addEventListener('click', () => cambiarZoom(0.25));
    btnMenos.addEventListener('click', () => cambiarZoom(-0.25));

    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') mover(1);
      if (e.key === 'ArrowLeft') mover(-1);
    });
    stage.addEventListener('click', () => root.focus({ preventScroll: true }));

    // Arrastrar en vez de scrollear.
    //
    // Con la hoja mas grande que la caja hacen falta las dos cosas, moverse por
    // la hoja y pasar pagina, y meterlas en un area desplazable creaba la
    // segunda barra que peleaba con la de la pagina. Asi que: si la hoja cabe,
    // el gesto pasa pagina; si no cabe, la mueve. La rueda del raton no se toca
    // nunca, sigue siendo de la pagina.
    let pan = { x: 0, y: 0 };
    let arrastre = null;

    function limites() {
      const caja = stage.getBoundingClientRect();
      const contenido = hojas.getBoundingClientRect();
      return {
        x: Math.max(0, (contenido.width - caja.width + 32) / 2),
        y: Math.max(0, (contenido.height - caja.height + 32) / 2),
      };
    }

    function aplicarPan() {
      const tope = limites();
      pan.x = Math.min(tope.x, Math.max(-tope.x, pan.x));
      pan.y = Math.min(tope.y, Math.max(-tope.y, pan.y));
      hojas.style.transform = 'translate(' + pan.x + 'px,' + pan.y + 'px)';
      stage.classList.toggle('pdf-reader__stage--pannable', tope.x > 1 || tope.y > 1);
    }

    function reiniciarPan() {
      pan = { x: 0, y: 0 };
      aplicarPan();
    }

    stage.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      const tope = limites();
      arrastre = {
        x: e.clientX,
        y: e.clientY,
        px: pan.x,
        py: pan.y,
        movible: tope.x > 1 || tope.y > 1,
        movido: false,
      };
      if (arrastre.movible) {
        stage.setPointerCapture(e.pointerId);
        stage.classList.add('pdf-reader__stage--panning');
      }
    });

    stage.addEventListener('pointermove', (e) => {
      if (!arrastre) return;
      const dx = e.clientX - arrastre.x;
      const dy = e.clientY - arrastre.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) arrastre.movido = true;
      if (!arrastre.movible) return;
      pan.x = arrastre.px + dx;
      pan.y = arrastre.py + dy;
      aplicarPan();
    });

    function soltar(e) {
      if (!arrastre) return;
      const dx = e.clientX - arrastre.x;
      // Con la hoja entera a la vista, el gesto lateral pasa pagina.
      if (!arrastre.movible && arrastre.movido && Math.abs(dx) > 45) {
        mover(dx < 0 ? 1 : -1);
      }
      stage.classList.remove('pdf-reader__stage--panning');
      arrastre = null;
    }

    stage.addEventListener('pointerup', soltar);
    stage.addEventListener('pointercancel', () => {
      stage.classList.remove('pdf-reader__stage--panning');
      arrastre = null;
    });

    if (btnFs) {
      if (!document.fullscreenEnabled) btnFs.hidden = true;
      btnFs.addEventListener('click', () => {
        if (document.fullscreenElement === root) document.exitFullscreen();
        else if (root.requestFullscreen) root.requestFullscreen().catch(() => {});
      });
      document.addEventListener('fullscreenchange', () => {
        const activo = document.fullscreenElement === root;
        btnFs.textContent = activo ? root.dataset.labelExit : root.dataset.labelEnter;
        cache.clear(); // cambia la caja, cambian los tamanos rasterizados
        pintar(0);
      });
    }

    let temporizador;
    addEventListener('resize', () => {
      clearTimeout(temporizador);
      temporizador = setTimeout(() => {
        cache.clear();
        pintar(0);
      }, 150);
    });

    (async () => {
      texto(root.dataset.labelLoading || '');
      try {
        await cargarScript(root.dataset.lib);
        const lib = window.pdfjsLib;
        lib.GlobalWorkerOptions.workerSrc = root.dataset.worker;
        pdf = await lib.getDocument({ url: url }).promise;
        total = pdf.numPages;
        const m = location.hash.match(/pagina=(\d+)/);
        if (m) hoja = Math.min(Math.max(1, +m[1]), total);
        texto('');
        await pintar(0);
      } catch (e) {
        texto(root.dataset.labelError || '');
      }
    })();
  }

  // No se carga PDF.js hasta que el lector se acerca a la pantalla: son 320 KB
  // de libreria mas el worker, y la pagina lleva debajo la rejilla de
  // colecciones. Sin IntersectionObserver se arranca directamente.
  document.querySelectorAll('[data-pdf-reader]').forEach((root) => {
    if (!('IntersectionObserver' in window)) {
      iniciar(root);
      return;
    }
    const observador = new IntersectionObserver((entradas) => {
      entradas.forEach((e) => {
        if (!e.isIntersecting) return;
        observador.disconnect();
        iniciar(root);
      });
    }, { rootMargin: '300px' });
    observador.observe(root);
  });
})();
